import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, Clock, LogIn } from 'lucide-react';
import { Button } from '../ui/button';
import { companyInfo } from '../../data/mock';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Accueil' },
    { path: '/services', label: 'Services' },
    { path: '/zones', label: 'Zones d\'intervention' },
    { path: '/a-propos', label: 'À propos' },
    { path: '/contact', label: 'Contact' }
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className="w-full">
      {/* Top Bar - Urgence - ROUGE */}
      <div className="bg-[#B91C1C] text-white py-2">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span className="font-semibold">Urgence 24h/24 - 7j/7 à Vitry-sur-Seine et Val-de-Marne</span>
          </div>
          <div className="flex items-center gap-4">
            <a 
              href={`tel:${companyInfo.phoneEmergency.replace(/\s/g, '')}`}
              className="flex items-center gap-2 hover:underline font-bold"
            >
              <Phone className="w-4 h-4" />
              {companyInfo.phoneEmergency}
            </a>
            <Link
              to="/connexion"
              className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 px-3 py-1 rounded-md font-semibold transition-colors"
              data-testid="header-login-link"
            >
              <LogIn className="w-4 h-4" />
              Se connecter
            </Link>
          </div>
        </div>
      </div>

      {/* Main Header - BLANC */}
      <div className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo EMS */}
            <Link to="/" className="flex items-center gap-3">
              <img 
                src={companyInfo.logo} 
                alt="EMS Express - Vitrier Serrurier Vitry-sur-Seine"
                className="h-14 w-auto object-contain"
              />
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-[#262626] leading-tight">
                  EMS Express
                </h1>
                <p className="text-xs text-[#666666]">
                  Miroiterie • Serrurerie
                </p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-[#B91C1C]/10 text-[#B91C1C]'
                      : 'text-[#262626] hover:bg-gray-100'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* CTA Buttons - ROUGE */}
            <div className="hidden md:flex items-center gap-3">
              <a href={`tel:${companyInfo.phoneEmergency.replace(/\s/g, '')}`}>
                <Button 
                  variant="outline" 
                  className="border-[#B91C1C] text-[#B91C1C] hover:bg-[#B91C1C] hover:text-white"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Appeler
                </Button>
              </a>
              <Link to="/contact">
                <Button className="bg-[#B91C1C] hover:bg-[#991B1B] text-white">
                  Devis gratuit
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-md hover:bg-gray-100"
              aria-label="Menu"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6 text-[#262626]" />
              ) : (
                <Menu className="w-6 h-6 text-[#262626]" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t">
            <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-[#B91C1C]/10 text-[#B91C1C]'
                      : 'text-[#262626] hover:bg-gray-100'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex flex-col gap-2 mt-4 pt-4 border-t">
                <a href={`tel:${companyInfo.phoneEmergency.replace(/\s/g, '')}`}>
                  <Button 
                    variant="outline" 
                    className="w-full border-[#B91C1C] text-[#B91C1C]"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    {companyInfo.phoneEmergency}
                  </Button>
                </a>
                <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                  <Button className="w-full bg-[#B91C1C] hover:bg-[#991B1B] text-white">
                    Devis gratuit
                  </Button>
                </Link>
                <Link to="/connexion" onClick={() => setIsMenuOpen(false)} data-testid="mobile-login-link">
                  <Button variant="outline" className="w-full border-gray-300 text-[#262626]">
                    <LogIn className="w-4 h-4 mr-2" />
                    Se connecter
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;